import {
  booleanSchema,
  dateSchema,
  listSchema,
  recordSchema,
  Storage,
  stringSchema,
  tableSchema,
  ValidationIssue
} from '@lightweightform/storage';

import * as lfKotlin from 'lf-kotlin-common-js';
import {format, isValid} from 'date-fns';

import {pt} from 'oss-registo-kotlin-common-js';

import {importStartDate, nextCommencementDate, now} from '../date/dates';
import {peculiarCountries} from '../catalog/countries';

const {
  CountryOfBusinessValidator,
  VatIdType,
  FIXED_ESTAB_VAT_ID_VALIDATOR,
  PREV_OR_CURR_OSS_VAT_IDS_VALIDATOR,
  PREV_OR_CURR_OSS_INT_IDS_VALIDATOR,
} = pt.at.oss.kotlin.manual.validator.common;

const FieldSizeConstants = pt.at.oss.kotlin.manual.enumeration.FieldSizeConstants;
const {
  ISSUING_COUNTRY_LEN,
  VAT_ID_NUMBER_MIN_LEN,
  VAT_ID_NUMBER_MAX_LEN,
  TAX_REF_NUM_MIN_LEN,
  TAX_REF_NUM_MAX_LEN,

  COMPANY_NAME_MIN_LEN,
  COMPANY_NAME_MAX_LEN,

  TRADING_NAME_MIN_LEN,
  TRADING_NAME_MAX_LEN,

  ADDRESS_FREE_TEXT_MAX_LEN,
  ADDRESS_STREET_MAX_LEN,
  ADDRESS_BUILDING_NUMBER_MAX_LEN,
  ADDRESS_FLOOR_NUMBER_MAX_LEN,
  ADDRESS_POST_CODE_MAX_LEN,
  ADDRESS_CITY_MAX_LEN,
  ADDRESS_SUITE_MAX_LEN,
  ADDRESS_POST_OFFICE_BOX_MAX_LEN,
  ADDRESS_DISTRICT_MAX_LEN,
  ADDRESS_COUNTRY_SUB_ENTITY_MAX_LEN,
  ADDRESS_OTHER_LOCAL_ID_MAX_LEN,

  WEBSITE_MIN_LEN,
  WEBSITE_MAX_LEN,

  CONTACT_NAME_FREE_TEXT_MAX_LEN,
  CONTACT_NAME_PRECEDING_TITLE_MAX_LEN,
  CONTACT_NAME_FIRST_NAME_MAX_LEN,
  CONTACT_NAME_LAST_NAME_PREFIX_MAX_LEN,
  CONTACT_NAME_LAST_NAME_MAX_LEN,
  CONTACT_NAME_GENERAL_SUFFIX_MAX_LEN,
  CONTACT_NAME_MAIDEN_NAME_MAX_LEN,
  CONTACT_NAME_TITLE_MAX_LEN,
  CONTACT_NAME_MIDDLE_NAME_MAX_LEN,
  CONTACT_NAME_GEN_IDENTIFIER_MAX_LEN,
  CONTACT_NAME_SUFFIX_MAX_LEN,

  CONTACT_INFO_EMAIL_MAX_LEN,
  CONTACT_INFO_TELEPHONE_MAX_LEN,

  BANK_ACCOUNT_OWNER_MAX_LEN,
  BANK_ACCOUNT_IBAN_MAX_LEN,
  BANK_ACCOUNT_OBAN_MAX_LEN,
  BANK_ACCOUNT_BIC_MAX_LEN,

  FIXED_ESTAB_TRADING_NAME_MIN_LEN,
  FIXED_ESTAB_TRADING_NAME_MAX_LEN,
} = FieldSizeConstants;

const PECULIAR_COUNTRIES = peculiarCountries.map(_ => _.codeA2);


export function getInitialValues(ctx: Storage): any {
  return ctx.getStateProperty('/', 'initialValues') || {};
}

export function registeredByInt(initialValues: any) {
  return initialValues && initialValues.registeredBy == 'INTERMEDIARY';
}

export function registeredByNetp(initialValues: any) {
  return !registeredByInt(initialValues);
}

/**
 * VAT ID Number / National Tax Number schema
 */
export function vatIdSchema(ktVatIdValidator, issuingCountryAsText: boolean = false) {
  const {
    isRequired,
    types,
    allowedCountryValuesArray: allowedCountries,
    allowedSchemesValuesArray: allowedSchemes,
    initialSchemeValue: initialScheme
  } = ktVatIdValidator;
  const vatIdNullable = !isRequired || types.includes(VatIdType.TAX); // includes the other type
  const taxRefNullable = !isRequired || types.includes(VatIdType.VAT); // includes the other type

  return recordSchema({
    scheme: stringSchema({
      allowedValues: allowedSchemes,
      initialValue: initialScheme,
      isNullable: true,
      isRequired: true,
    }),
    issuingCountry: stringSchema({
      minLength: ISSUING_COUNTRY_LEN.toInt(),
      maxLength: ISSUING_COUNTRY_LEN.toInt(),
      allowedValues: allowedCountries,
      isNullable: !issuingCountryAsText,
      isRequired: issuingCountryAsText ? undefined : isRequired,
      initialValue: issuingCountryAsText ? '' : null
    }),
    vatIdNum: stringSchema({
      minLength: vatIdNullable ? 0 : VAT_ID_NUMBER_MIN_LEN.toInt(),
      maxLength: VAT_ID_NUMBER_MAX_LEN.toInt(),
      validate: [
        ctx => ktVatIdValidator.vatIdNumIsValid(ctx.get()) ?? undefined,
        ...(ktVatIdValidator.validateVatIdIsAllowed
          ? [ctx => ktVatIdValidator.validateVatIdIsAllowed(ctx.get(), ctx.get('../issuingCountry')) ?? undefined]
          : []),
        ctx => ktVatIdValidator.validateVatIdNumFormat(ctx.get(), ctx.get('../scheme'), ctx.get('../issuingCountry')) ?? undefined
      ]
    }),
    taxRefNum: stringSchema({
      minLength: taxRefNullable ? 0 : TAX_REF_NUM_MIN_LEN.toInt(),
      maxLength: TAX_REF_NUM_MAX_LEN.toInt(),
      validate: [
        ctx => ktVatIdValidator.taxRefNumIsValid(ctx.get()) ?? undefined,
        ...(ktVatIdValidator.validateTaxRefIsAllowed
          ? [ctx => ktVatIdValidator.validateTaxRefIsAllowed(ctx.get(), ctx.get('../issuingCountry')) ?? undefined]
          : []),
        ctx => ktVatIdValidator.validateTaxRefFormat(ctx.get()) ?? undefined
      ]
    })
  }, {
    validate: ctx => ktVatIdValidator.vatIdIsValid(ctx.get('./issuingCountry'), ctx.get('./vatIdNum'), ctx.get('./taxRefNum')),
  });
}


/**
 * Address schema
 */
const {AddressType} = pt.at.oss.kotlin.manual.enumeration;
const AddressValidator = pt.at.oss.kotlin.manual.validator.common.AddressValidator;
const ADDRESS_COUNTRIES = AddressValidator.Companion.ADDRESS_COUNTRIES.map(_ => _.codeA2);
const addressValidator = new AddressValidator();

function addrAllowIffStructured(ctx: Storage): ValidationIssue {
  const isStructured = ctx.get('../type') === AddressType.STRUCTURED.value;
  const fieldValue = ctx.get();
  return addressValidator.validateStructuredFieldProhibition(fieldValue, isStructured) ?? undefined;
}

function validatePostCodeFormatIf(postCodeCtx: Storage): ValidationIssue {
  const shouldValidate = formattedPostCodeRequired(postCodeCtx.relativeStorage('..'));
  const postalCode = postCodeCtx.get();
  return addressValidator.validatePostCodeFormatIf(postalCode, shouldValidate) ?? undefined;

}

function formattedPostCodeRequired(addressCtx: Storage): boolean {
  const isStructured = addressCtx.get('./type') === AddressType.STRUCTURED.value;
  const country = addressCtx.get('./country');
  return addressValidator.formattedPostCodeRequired(isStructured, country);
}

export const addressSchema = recordSchema({
  type: stringSchema({
    allowedValues: AddressType.values().map(_ => _.value),
    initialValue: AddressType.STRUCTURED.value,
    isNullable: true, isRequired: true
  }),

  street: stringSchema({
    maxLength: ADDRESS_STREET_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  buildingNumber: stringSchema({
    maxLength: ADDRESS_BUILDING_NUMBER_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  floorNumber: stringSchema({
    maxLength: ADDRESS_FLOOR_NUMBER_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  postCode: stringSchema({
    minLength: ctx => formattedPostCodeRequired(ctx) ? 1 : 0,
    maxLength: ADDRESS_POST_CODE_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      validatePostCodeFormatIf,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  city: stringSchema({
    minLength: ctx => ctx.get('./type') === AddressType.STRUCTURED.value ? 1 : 0,
    maxLength: ADDRESS_CITY_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  country: stringSchema({
    allowedValues: ADDRESS_COUNTRIES, isNullable: true, isRequired: true,
    minLength: ISSUING_COUNTRY_LEN.toInt(), maxLength: ISSUING_COUNTRY_LEN.toInt()
  }),
  region: stringSchema({
    isNullable: true,
    allowedValues: PECULIAR_COUNTRIES,
    validate: ctx => addressValidator.validateRegion(ctx.get(), ctx.get('../country')) ?? undefined
  }),

  // Extra fields
  suite: stringSchema({
    maxLength: ADDRESS_SUITE_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  district: stringSchema({
    maxLength: ADDRESS_DISTRICT_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  postOfficeBox: stringSchema({
    maxLength: ADDRESS_POST_OFFICE_BOX_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  countrySubEntity: stringSchema({
    maxLength: ADDRESS_COUNTRY_SUB_ENTITY_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  otherLocalId: stringSchema({
    maxLength: ADDRESS_OTHER_LOCAL_ID_MAX_LEN.toInt(),
    validate: [
      addrAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  // Free text
  freeText: stringSchema({
    minLength: ctx => ctx.get('./type') !== AddressType.STRUCTURED.value ? 1 : 0,
    maxLength: ADDRESS_FREE_TEXT_MAX_LEN.toInt(),
    validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
  })
});


/**
 * Contact name schema
 */
const {ContactNameType} = pt.at.oss.kotlin.manual.enumeration;
const {ContactNameValidator} = pt.at.oss.kotlin.manual.validator.common;

function nameAllowIffStructured(ctx: Storage): ValidationIssue {
  const isStructured = ctx.get('../type') === ContactNameType.STRUCTURED.value;
  const fieldValue = ctx.get();
  return ContactNameValidator.validateStructuredFieldProhibition(fieldValue, isStructured) ?? undefined;
}

function nameMultiAllowIffStructured(ctx: Storage): ValidationIssue {
  const isStructured = ctx.get('../type') === ContactNameType.STRUCTURED.value;
  const fieldValue = ctx.get();
  return ContactNameValidator.validateMultiStructuredFieldProhibition(fieldValue.length, isStructured) ?? undefined;
}

const contactNameSchema = recordSchema({
  type: stringSchema({
    allowedValues: ContactNameType.values().map(_ => _.value),
    initialValue: ContactNameType.FREE_TEXT.value,
    isNullable: true, isRequired: true
  }),

  // Single values
  precedingTitle: stringSchema({
    maxLength: CONTACT_NAME_PRECEDING_TITLE_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  firstName: stringSchema({
    minLength: ctx => ctx.get('./type') === ContactNameType.STRUCTURED.value ? 1 : 0,
    maxLength: CONTACT_NAME_FIRST_NAME_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  lastNamePrefix: stringSchema({
    maxLength: CONTACT_NAME_LAST_NAME_PREFIX_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  lastName: stringSchema({
    minLength: ctx => ctx.get('./type') === ContactNameType.STRUCTURED.value ? 1 : 0,
    maxLength: CONTACT_NAME_LAST_NAME_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  generalSuffix: stringSchema({
    maxLength: CONTACT_NAME_GENERAL_SUFFIX_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  maidenName: stringSchema({
    maxLength: CONTACT_NAME_MAIDEN_NAME_MAX_LEN.toInt(),
    validate: [
      nameAllowIffStructured,
      ctx  => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    ]
  }),

  // Multiple values
  // Note: nameMultiAllowIffStructured is in listSchema's validate NOT recordSchema's.
  titles: listSchema(recordSchema({
    name: stringSchema({
      minLength: 1,
      maxLength: CONTACT_NAME_TITLE_MAX_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    })
  }), {
    validate: nameMultiAllowIffStructured
  }),

  middleNames: listSchema(recordSchema({
    name: stringSchema({
      minLength: 1,
      maxLength: CONTACT_NAME_MIDDLE_NAME_MAX_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    })
  }), {
    validate: nameMultiAllowIffStructured
  }),

  generationIdentifiers: listSchema(recordSchema({
    name: stringSchema({
      minLength: 1,
      maxLength: CONTACT_NAME_GEN_IDENTIFIER_MAX_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    })
  }), {
    validate: nameMultiAllowIffStructured
  }),

  suffixes: listSchema(recordSchema({
    name: stringSchema({
      minLength: 1,
      maxLength: CONTACT_NAME_SUFFIX_MAX_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    })
  }), {
    validate: nameMultiAllowIffStructured
  }),

  // Free text
  freeText: stringSchema({
    minLength: ctx => ctx.get('./type') !== ContactNameType.STRUCTURED.value ? 1 : 0,
    maxLength: CONTACT_NAME_FREE_TEXT_MAX_LEN.toInt(),
    validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
  }),
});


/**
 * Bank account info schema
 */
const {
  BankAccountInfoValidator,
  FixedEstablishmentsValidator
} = pt.at.oss.kotlin.manual.validator.common;

export function bankAccountInfoSchema(allowOban: boolean, isNullable: boolean = false) {
  return recordSchema({
    iban: stringSchema({
      minLength: allowOban ? 0 : 1,
      maxLength: BANK_ACCOUNT_IBAN_MAX_LEN.toInt(),
      validate: [
        ctx => BankAccountInfoValidator.validateIban(ctx.get()) ?? undefined,
        ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
      ]
    }),

    bic: stringSchema({
      minLength: 1, maxLength: BANK_ACCOUNT_BIC_MAX_LEN.toInt(),
      validate: ctx => BankAccountInfoValidator.validateBic(ctx.get()) ?? undefined
    }),

    oban: stringSchema({
      maxLength: allowOban ? BANK_ACCOUNT_OBAN_MAX_LEN.toInt() : 0,
      validate: [
        ctx => BankAccountInfoValidator.validateOban(ctx.get(), allowOban) ?? undefined,
        ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
      ]
    }),

    accountHolder: stringSchema({
      minLength: 1, maxLength: BANK_ACCOUNT_OWNER_MAX_LEN.toInt(),
      validate: ctx => BankAccountInfoValidator.validateAccountHolder(ctx.get()) ?? undefined
    }),
  }, {
    validate: ctx => BankAccountInfoValidator.validateMutexIbanOban(ctx.get().iban, ctx.get().oban) ?? undefined,
    isNullable,
  });
}


/**
 * Fixed establishments schema
 */
export const fixedEstablishmentsSchema = (forceEstablished: boolean) => tableSchema(
  recordSchema({
    taxIdentif: vatIdSchema(FIXED_ESTAB_VAT_ID_VALIDATOR), // all MSs except MSID

    hasRealEstablishment: booleanSchema({
      allowedValues: ctx =>
        forceEstablished
          ? [true]
          : (ctx.get('../../notEstablishedInEU') ? [false] : [true, false]),
      initialValue: ctx => forceEstablished
        ? true
        : !ctx.get('../../notEstablishedInEU')
    }),

    tradingName: stringSchema({
      minLength: FIXED_ESTAB_TRADING_NAME_MIN_LEN.toInt(), maxLength: FIXED_ESTAB_TRADING_NAME_MAX_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    }),

    address: addressSchema,
  }),
  {
    validate: ctx => new FixedEstablishmentsValidator().validateDuplicateFixedEstablishments(ctx.get().map(estab => estab.taxIdentif.issuingCountry)) ?? undefined,
  }
);


const {DateStringSchema} = lfKotlin.pt.lightweightform.kotlin.schema;
const {
  TradingNamesValidator,
  ContactInfoValidator,
  SupplyCommencementValidator,
  WebsitesValidator,
  FreeTextValidator
} = pt.at.oss.kotlin.manual.validator.common;

export enum OssSchemaType {
  UNION,
  NON_UNION,
  IMPORT,
  INTERMEDIARY,
}

/**
 * Common registration form schema.
 */
export function makeRegistrationSchema(extraFields, schemaType: OssSchemaType, extraValidates: Array<any> = []) {

  return recordSchema({

    ...extraFields,

    ...(schemaType !== OssSchemaType.INTERMEDIARY ? {
      prevOrCurrOssVatId: tableSchema(vatIdSchema(PREV_OR_CURR_OSS_VAT_IDS_VALIDATOR)),
    } : {
      prevOrCurrOssVatId: tableSchema(vatIdSchema(PREV_OR_CURR_OSS_INT_IDS_VALIDATOR)),
    }),

    ...(schemaType === OssSchemaType.UNION || schemaType === OssSchemaType.NON_UNION ? {
      supplyCommencement: recordSchema({
        alreadyCarriedOutSupplies: booleanSchema({initialValue: false, isNullable: true, isRequired: true}),
        commencementDate: dateSchema({
          isNullable: true,
          isRequired: ctx => ctx.get('./alreadyCarriedOutSupplies'),
          validate: ctx => {
            const dateFormatted = isValid(ctx.get())
              ? format(ctx.get(), DateStringSchema.Companion.DEFAULT_DATE_FORMAT)
              : null;
            return SupplyCommencementValidator.validateCommencementDateValue(dateFormatted, ctx.get('../alreadyCarriedOutSupplies'));
          }
        })
      })
    } : {}),

    meetsConditions: booleanSchema({
      isClientOnly: true,
      allowedValues: ctx =>
        (ctx.getStateProperty('/', 'ignoreMeetsConditions') || !getInitialValues(ctx).mode.new)
          ? undefined : [true]
    }),

    companyName: stringSchema({
      maxLength: COMPANY_NAME_MAX_LEN.toInt(),
      minLength: COMPANY_NAME_MIN_LEN.toInt(),
      validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
    }),

    tradingNames: listSchema(recordSchema({
      name: stringSchema({
        minLength: TRADING_NAME_MIN_LEN.toInt(),
        maxLength: TRADING_NAME_MAX_LEN.toInt(),
        validate: ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
      })
    })),

    countryOfBusiness: stringSchema({
      isNullable: true, isRequired: true,
      minLength: ISSUING_COUNTRY_LEN.toInt(),
      maxLength: ISSUING_COUNTRY_LEN.toInt(),
      validate: [
        ctx => {
          const initialValues = getInitialValues(ctx)
          const allowMSNoRegion = schemaType === OssSchemaType.IMPORT && registeredByInt(initialValues);
          return new CountryOfBusinessValidator().countryOfBusinessValidate(ctx.get(), ctx.get('../cobRegion'), allowMSNoRegion) ?? undefined;
        },
        ctx => new CountryOfBusinessValidator().allowPTValidate(ctx.get(), schemaType !== OssSchemaType.NON_UNION) ?? undefined
      ]
    }),
    cobRegion: stringSchema({
      isNullable: true,
      validate: ctx => new CountryOfBusinessValidator().validateRegion(ctx.get(), ctx.get('../countryOfBusiness')) ?? undefined
    }),

    address: addressSchema,

    ...(schemaType !== OssSchemaType.INTERMEDIARY ? {
      websites: listSchema(
        recordSchema({
          name: stringSchema({
            minLength: WEBSITE_MIN_LEN.toInt(),
            maxLength: WEBSITE_MAX_LEN.toInt(),
          })
        }, {
          validate: ctx => WebsitesValidator.validateWebsite(ctx.get('./name')) ?? undefined
        }), {
          minSize: schemaType == OssSchemaType.NON_UNION || schemaType == OssSchemaType.IMPORT ? 1 : 0
        })
    } : {}),

    contactName: contactNameSchema,

    contactInfo: recordSchema({
      email: stringSchema({
        minLength: 1,
        maxLength: CONTACT_INFO_EMAIL_MAX_LEN.toInt(),
        validate: [
          ctx => ContactInfoValidator.validateEmail(ctx.get()) ?? undefined,
          ctx => FreeTextValidator.validateFreeText(ctx.get()) ?? undefined
        ]
      }),
      emailConfirmation: stringSchema({
        isClientOnly: true,
        minLength: 1,
        validate: ctx => ContactInfoValidator.validateEmailConfirmation(ctx.get(), ctx.get('../email')) ?? undefined,
      }),
      telephone: stringSchema({
        minLength: 1,
        maxLength: CONTACT_INFO_TELEPHONE_MAX_LEN.toInt(),
        validate: ctx => ContactInfoValidator.validateTelephone(ctx.get()) ?? undefined,
      }),
    }),

    relevantDates: recordSchema({
      registrationReqDate: dateSchema({
        isNullable: true,
        computedValue: ctx => {
          const isDetail = getInitialValues(ctx).mode.detail;
          const initialValue = ctx.getStateProperty('/', 'initialValues')?.preFilledForm?.relevantDates.registrationReqDate;
          if (isDetail) {
            return initialValue;
          } else {
            return initialValue ? initialValue : now();
          }
        }
      }),
      forecastStartDate: dateSchema({
        isNullable: true,
        computedValue: ctx => {
          const isDetail = getInitialValues(ctx).mode.detail;
          const filledCommencementDate = schemaType === OssSchemaType.INTERMEDIARY || schemaType === OssSchemaType.IMPORT
            ? null : ctx.get('../supplyCommencement/commencementDate');
          const initialValue = ctx.getStateProperty('/', 'initialValues')?.preFilledForm?.relevantDates.forecastStartDate;

          if (isDetail) {
            return initialValue;

          } else if (filledCommencementDate) {
            return filledCommencementDate;

          } else if (initialValue) {
            return initialValue;

          } else {
            if (OssSchemaType.IMPORT === schemaType || OssSchemaType.INTERMEDIARY === schemaType) {
              return importStartDate(now());

            }
            return nextCommencementDate(now());
          }
        }
      }),
      msidDecisionDate: dateSchema({
        isNullable: true,
        computedValue: ctx => {
          const isDetail = getInitialValues(ctx).mode.detail;
          const initialValue = ctx.getStateProperty('/', 'initialValues')?.preFilledForm?.relevantDates.msidDecisionDate;
          if (isDetail) {
            return initialValue;
          } else {
            return initialValue ? initialValue : null;
          }
        }
      })
    }, {isClientOnly: true}),

  }, {
    validate: [
      ctx => TradingNamesValidator.tradingNamesValidate(ctx.get('./tradingNames').length, ctx.get('./companyName')) ?? undefined,
      ...extraValidates
    ]
  });
}

